/**
 * render-core 的字段元信息：只保留渲染真正用到的三个属性。
 *
 * 刻意不从 `@lark-base-open/js-sdk` 取 `IFieldMeta` —— 那会把整个 SDK
 * （及其浏览器假设）拖进这个包，而这个包必须能在 Cloudflare Worker 里
 * 跑。SDK 的 `IFieldMeta` 结构上是它的超集，`IFieldMeta[]` 可以直接赋给
 * `FieldMeta[]`，所以侧边栏那边零改动。
 */
export interface FieldMeta {
  id: string;
  name: string;
  /** 飞书字段类型枚举值（SDK 的 FieldType）。 */
  type: number;
  /**
   * 字段的格式化配置，渲染时按它输出。只读这几个键：
   *   formatter      数字/百分比格式，如 '0.00' / '1,000' / '0.00%'
   *   decimalDigits  货币小数位
   *   currencyCode   货币代码，如 'CNY'
   *   dateFormat     日期格式，如 'yyyy/MM/dd'（没有 HH 就不印时间）
   * 不带它的话，表里配的「百分比 / 两位小数 / 只显示日期」在单据上全都失效。
   */
  property?: unknown;
}

// ---- Section types (discriminated union) ----

export interface HeaderSection {
  type: 'header';
  id: string;
  content: string;
  align: 'left' | 'center' | 'right';
  fontSizePx: number | null;
  bold: boolean;
}

export interface InfoGridItem {
  id: string;
  label: string;
  fieldId: string | null;
  staticValue: string;
}

export interface InfoGridSection {
  type: 'info_grid';
  id: string;
  columns: 1 | 2 | 3;
  items: InfoGridItem[];
}

export interface DetailColumn {
  id: string;
  fieldId: string;
  label: string;
  width: string | null;
  align: 'left' | 'center' | 'right';
}

export interface SummaryConfig {
  label: string;
  sumColumns: string[];
}

export interface DetailTableSection {
  type: 'detail_table';
  id: string;
  linkFieldId: string;
  linkedTableId: string;
  columns: DetailColumn[];
  showIndex: boolean;
  summaryRow: SummaryConfig | null;
  /** 明细排序：按某列升/降序（columnId 为 DetailColumn.id）。 */
  sort?: { columnId: string; dir: 'asc' | 'desc' } | null;
  /** 明细筛选：只保留满足条件的行（columnId 为 DetailColumn.id）。 */
  filter?: { columnId: string; op: 'eq' | 'ne' | 'contains' | 'gt' | 'lt'; value: string } | null;
}

export interface TextBlockSection {
  type: 'text_block';
  id: string;
  content: string;
  fontSizePx: number | null;
  /** 对齐，缺省靠左（老模板没有这个字段）。 */
  align?: 'left' | 'center' | 'right';
}

/** 自由表格的一个格子。内容是纯文本，支持 {{字段名}} 占位与换行。
 *  被左侧/上方的合并区吃掉的格子仍然留在数组里（merged=true），渲染时跳过——
 *  这样每行的 cells 长度恒等于列数，增删行列只是改标记，不用重排。 */
export interface FreeTableCell {
  id: string;
  content: string;
  colSpan: number;
  rowSpan: number;
  align: 'left' | 'center' | 'right';
  bold: boolean;
  /** 灰底（表头行/标签列）。 */
  shaded: boolean;
  merged: boolean;
  /** 文字颜色，CSS 色值；缺省 = 继承。 */
  color?: string | null;
  /** 背景色，CSS 色值。设了它就盖过 shaded 的灰底。 */
  bg?: string | null;
  /** 这一格单独的字号；缺省 = 跟表格。 */
  fontSizePx?: number | null;
}

export interface FreeTableRow {
  id: string;
  /** 长度恒等于 colWidths.length，含被合并的占位格。 */
  cells: FreeTableCell[];
  /** 行高 mm，null = 按内容自适应。 */
  heightMm: number | null;
}

/**
 * 自由表格：可合并单元格、可定列宽的一整张框线表，用来还原 Word 里那种
 * 国标表单 / 作业票（一张连续大表，标签和填写栏交错跨行跨列）。
 * 信息网格做不到跨行跨列合并、相邻区块也不会拼成一张表，才有这个区块。
 */
/**
 * 自由表格里那一行「样板行」的数据来源。
 *
 * 为什么加它：明细表格能带子表多行但不能合并单元格，自由表格能合并但只能填
 * 主记录的字段。客户要的「左边一个跨行的合并格 + 右边随子表自动加行的明细」
 * 两边都做不到。现在让自由表格的某一行认领一个关联字段当样板行，打印时按子表
 * 条数向下复制 —— 合并、跨行、明细三件事第一次能同时成立。
 */
/**
 * 表格的数据来源。三选一，对应用户脑子里真实的三种问法：
 *   static  行数固定，每格自己填 —— 表单式的格子区、签字栏
 *   link    一行 = 当前记录关联的一条子表记录 —— 一张单带多行商品
 *   records 一行 = 本表的另一条记录 —— 多条记录摊成一张清单
 * 不管选哪种，合并单元格、跨行跨列、列宽都照常可用。
 */
export type TableSource = 'static' | 'link' | 'records';

export interface FreeTableRepeat {
  /** 数据来源。缺省按 'link' 解释（这个字段是后加的，老模板都是关联子表）。 */
  source?: TableSource;
  /** source='link' 时的关联字段（id 或名称）。子表的每一条记录复制一份样板行。 */
  linkFieldId: string;
  /** 样板行在 rows 里的下标。 */
  rowIndex: number;
  /** 列下标 → 字段（id 或名称）。没配的列按静态内容原样复制。 */
  columnFields: Record<number, string>;
  /** 最多印多少行，防止误配了一个上万条的关联字段。 */
  maxRows?: number;
  /** source='records' 时取本表哪些记录。 */
  recordRange?: 'visible' | 'selected' | 'all';
}

/** 表格底部的合计行。列下标 → 是否求和。 */
export interface TableSummary {
  label: string;
  /** 要求和的列下标。 */
  columns: number[];
}

export interface FreeTableSection {
  type: 'free_table';
  id: string;
  /** 各列宽度百分比，长度 = 列数，和为 100。 */
  colWidths: number[];
  rows: FreeTableRow[];
  /** 框线粗细 px，0 = 无框线。 */
  borderPx: number;
  fontSizePx: number | null;
  /** 动态明细：让某一行随数据源自动加行。缺省 = 纯静态表（老行为）。 */
  repeat?: FreeTableRepeat | null;
  /** 合计行：在动态行下面加一行小计。 */
  summary?: TableSummary | null;
  /** 动态行加斑马纹，长明细看着不串行。 */
  zebra?: boolean;
  /** 末尾显示「共 N 条」。 */
  showCount?: boolean;
}

export interface SignatureParty {
  id: string;
  label: string;
  rows: Array<{ id: string; label: string; fieldId: string | null; staticValue: string }>;
  /** 印章图 data URL（用户上传，存模板里）；null/缺省 = 不盖章。 */
  sealDataUrl?: string | null;
  /** 章直径（mm）。默认 40 —— 公章实务尺寸（刻章店实物），不是国标 30。 */
  sealSizeMm?: number;
}

export interface SignatureSection {
  type: 'signature';
  id: string;
  parties: SignatureParty[];
  /**
   * 只有一方落款时放哪边。'right' = 靠右半边（对账单、送货单「我方盖章」的习惯）；
   * 缺省 = 老行为，铺满整行从左排。多方时忽略，始终并排均分。
   */
  align?: 'right';
}

export interface DividerSection {
  type: 'divider';
  id: string;
  style: 'line' | 'page_break' | 'blank';
}

/** 文章区块：把字段里的 HTML / Markdown 渲染成富文本（AI 生成内容、报告）。 */
export interface RichTextSection {
  type: 'rich_text';
  id: string;
  /** 绑定字段（内容源）。 */
  fieldId: string | null;
  /** 内容格式。 */
  format: 'markdown' | 'html';
}

/** 二维码 / 条形码：内容取字段值或静态文本，扫码出库/溯源场景。 */
export interface CodeSection {
  type: 'code';
  id: string;
  codeType: 'qr' | 'barcode';
  /** 绑定字段（优先）；无则用 staticValue。 */
  fieldId: string | null;
  staticValue: string;
  /** 二维码边长 / 条形码高度（px）。 */
  sizePx: number;
  align: 'left' | 'center' | 'right';
  /** 条形码是否显示下方文字。 */
  showText: boolean;
}

/** 附件图片区块：把主记录附件字段里的图片排进文档（发票/凭证/照片场景）。
 *  非图片附件降级为文件名列表。 */
export interface AttachmentSection {
  type: 'attachment';
  id: string;
  fieldId: string | null;
  title: string;
  /** full = 每张占满一行（凭证/发票）；grid2 = 两列网格（照片）。 */
  layout: 'full' | 'grid2';
  /** 单张图最大高度（mm），null = 不限制（只按宽度缩放）。 */
  maxHeightMm: number | null;
  showName: boolean;
  /** 每张图片前强制分页（发票一张一页的打印习惯）。 */
  pageBreakEach: boolean;
}

/**
 * 分栏：几栏并排，每栏里可以再放任何区块（文字、字段、表格、图片……）。
 * 「甲方 | 乙方」这种并排版面靠它。宽度按百分比分，打印时是一张无框表。
 */
export interface ColumnsSection {
  type: 'columns';
  id: string;
  /** 各栏宽度百分比，和为 100。 */
  widths: number[];
  /** 每栏里的区块，长度 = widths.length。 */
  columns: ContractSection[][];
  /** 栏间距 mm。 */
  gapMm?: number;
}

export type ContractSection =
  | ColumnsSection
  | HeaderSection
  | InfoGridSection
  | DetailTableSection
  | FreeTableSection
  | TextBlockSection
  | SignatureSection
  | DividerSection
  | AttachmentSection
  | CodeSection
  | RichTextSection;

export type ContractSectionType = ContractSection['type'];

// ---- 自由元素层（覆盖在文档流之上，坐标 mm，原点 = 正文区左上角） ----

export interface OverlayBase {
  id: string;
  /**
   * 位置，mm。x 恒为距正文区左边；y 的起点看 anchor：
   *   'page'（缺省，老模板）  = 距页面顶部
   *   'content_end'          = 距内容结束处（可为负）
   *   'section'              = 距 anchorSectionId 那个顶层区块的顶边
   */
  xMm: number;
  yMm: number;
  wMm: number;
  hMm: number;
  /**
   * 纵向基准。固定在页顶的章，明细多了就盖到表格中间；跟着内容末尾的章，
   * 在明细多的记录上摆好、换到明细少的记录就飞出页顶（看起来「章没了」）。
   * 'section' 才是对的：章跟着它压住的那个区块走（像 Word 的「锚定到段落」），
   * 画布上拖一次就自动挂到落点下面的区块上。
   */
  anchor?: 'page' | 'content_end' | 'section';
  /** anchor='section' 时挂靠的顶层区块 id。 */
  anchorSectionId?: string;
}

/** 印章：自由摆放，multiply 混合透字。 */
export interface SealOverlay extends OverlayBase {
  kind: 'seal';
  dataUrl: string;
}

/** 签名区域：给签署人预留的落笔位置（发起签名时把坐标传给签字服务）。
 *  打印/导出时不显示边框，仅在预览/编辑态显示虚线占位。 */
export interface SignAreaOverlay extends OverlayBase {
  kind: 'sign_area';
  /** 签署方标签，如「甲方」「仓库核验」。 */
  label: string;
}

export type OverlayItem = SealOverlay | SignAreaOverlay;

// ---- Contract Template ----

export interface ContractPrintSettings {
  paperSize: 'A4' | 'A5' | 'A3' | 'Letter';
  orientation: 'portrait' | 'landscape';
  marginMm: number;
  baseFontSizePx: number;
  /** 水印文字（防伪/草稿标记）；空 = 无水印。 */
  watermarkText?: string;
  /** 图片水印 data URL（公司 logo 淡化居中）；与文字水印可并存。 */
  watermarkImage?: string;
  /** 页眉文字（每页顶部重复）；空 = 无。 */
  headerText?: string;
  /** 页脚文字（每页底部重复）；空 = 无。**没有页码占位**：页码要靠浏览器
   *  打印设置里的「页眉页脚」开关，我们的渲染管线（截图转 PDF）拿不到页号。 */
  footerText?: string;
  /** 连续页面：导出 PDF 不分页，按内容自适应高度成一长页（长报价单等）。 */
  continuousPage?: boolean;
  /** 自定义导出文件名模板，支持 {字段名} 变量；空 = 默认（表名_首字段）。 */
  exportFileName?: string;
}

/** 条件显隐规则：按主记录某字段的值决定区块是否渲染。 */
export interface VisibilityRule {
  fieldId: string;
  op: 'notEmpty' | 'empty' | 'eq' | 'ne' | 'contains';
  /** eq/ne/contains 需要的比较值。 */
  value?: string;
}

export interface ContractTemplate {
  id: string;
  tableId: string;
  name: string;
  sections: ContractSection[];
  /** 自由元素层（印章/签名区域），老模板无此字段。 */
  overlays?: OverlayItem[];
  /** sectionId → 显隐规则（不满足则该区块不渲染）。 */
  visibility?: Record<string, VisibilityRule>;
  settings: ContractPrintSettings;
  createdAt: number;
  updatedAt: number;
  /**
   * 共享给同企业的人。打开后同企业任何人打开插件都能看到并一起改（谁改得晚
   * 谁算数）。竞品「一人做版式全员用」靠的就是这个；没有它，团队里每个人
   * 都得各自照着搭一遍。
   */
  shared?: boolean;
  /** 谁把它共享出来的（accountId）。只有这个人能取消共享。 */
  sharedBy?: string;
}

// ---- Resolved data (at render time) ----

export interface LinkedTableData {
  tableId: string;
  fieldMetas: FieldMeta[];
  records: Array<{
    recordId: string;
    cells: Record<string, unknown>;
    /**
     * fieldId → 该行附件列解析出的可加载图片。取数阶段只对附件类型的列填；
     * 没填（老数据/取 URL 失败）时明细表格退回打印文件名。
     */
    attachments?: Record<string, AttachmentItem[]>;
  }>;
}

export interface AttachmentItem {
  name: string;
  /** 首选地址。侧边栏里是我们自己的图片代理，服务端回源，能绕开过期/第三方 Cookie/referrer。 */
  url: string;
  /**
   * 代理取不到时回退用的原始飞书临时 URL。
   *
   * 存在的意义是「不许比修之前更糟」：代理若因为域名不在白名单、或那个 URL
   * 依赖浏览器 Cookie 而失败，至少还能退回浏览器直连——那正是加代理之前的行为。
   */
  fallbackUrl?: string;
  /** 文件 token + 来源坐标：独立页没有 SDK，靠它们让 worker 现取下载地址（临时 URL 几分钟就过期）。 */
  token?: string;
  ref?: { tableId: string; fieldId: string; recordId: string };
  /** MIME type，image/* 才内嵌渲染。 */
  mimeType: string;
}

export interface ContractRenderData {
  mainRecord: Record<string, unknown>;
  mainFieldMetas: FieldMeta[];
  linkedRecords: Record<string, LinkedTableData>;
  /** sectionId → 该附件区块解析出的附件列表。 */
  attachments: Record<string, AttachmentItem[]>;
  /**
   * 主记录里附件字段 fieldId → 可加载的图片。自由表格 / 信息网格里用
   * {{附件字段}} 引用时按这个出图；没解析到就退回打印文件名。
   */
  mainAttachments?: Record<string, AttachmentItem[]>;
  /** sectionId → 二维码/条形码预生成的图片 data URL（渲染器同步取用）。 */
  codes?: Record<string, string>;
  /**
   * 示例模式：没有选中记录时也要让编辑者看见排版。字段值渲染成
   * 〔字段名〕占位、条件显隐一律放行；output 模式会强制关掉它，
   * 成品里绝不会印出占位符。
   */
  sampleMode?: boolean;
  /**
   * 对照底图：把一张纸质单据的扫描件/照片垫在画布下面，照着它对齐标题、
   * 表格和签字位置。**只在编辑态出现，打印和导出绝不会带上**。
   *
   * 刻意不存进模板：底图动辄几 MB，塞进模板会撑爆 localStorage、拖垮云同步，
   * 还会跟着模板被别人下载。它按模板 id 存在本机（见 src/utils/underlay.ts），
   * 换设备要重新传一次 —— 这个取舍和竞品一致。
   */
  underlay?: { dataUrl: string; opacity: number } | null;
  /**
   * 编辑态把 {{字段}} 画成胶囊而不是渲染成值。
   *
   * 画布上直接显示真实值好看，但用户看不出哪一段是绑定的字段、哪一段是死文本 ——
   * 「为什么换条记录这里不变」就是这么来的。编辑时显示结构、预览时显示数据，
   * 两个模式各司其职。
   */
  fieldChips?: boolean;
}

// ---- Helpers ----

export const SECTION_TYPE_LABELS: Record<ContractSectionType, string> = {
  header: '标题/抬头',
  info_grid: '信息网格',
  // 明细表格是旧版：它带得了子表多行，却不能合并单元格。新版「表格」用
  // 数据来源下拉覆盖了它的全部能力，所以新建时不再提供，只保留渲染与编辑，
  // 并在编辑器里给一键升级。
  detail_table: '明细表格（旧版）',
  free_table: '表格',
  text_block: '文本段落',
  signature: '签章区',
  attachment: '附件图片',
  code: '二维码/条码',
  rich_text: '文章区块',
  divider: '分隔线',
  columns: '分栏',
};

/** 新建区块时可选的类型。明细表格已被「表格」取代，不再新增。 */
export const INSERTABLE_SECTION_TYPES: ContractSectionType[] = [
  'header',
  'info_grid',
  'free_table',
  'text_block',
  'signature',
  'attachment',
  'code',
  'rich_text',
  'divider',
  'columns',
];

let _counter = 0;
export function genId(): string {
  return `${Date.now().toString(36)}_${(++_counter).toString(36)}`;
}
